import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import { Button, Loader, Modal } from 'semantic-ui-react';
import { TextField, MenuItem, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Autocomplete from '@mui/material/Autocomplete';

import ErrorMessage from '../../../components/Error/Error';
import { useUserDispatch, tabActive } from '../../../context/UserContext';
import { listMasterCertificate, addMasterCertificate, editMasterCertificate, listLedgerParties } from '../../../api/api';
import { masterCertificateTypes, masterCertificateStatus, addValidationSchema, editValidationSchema } from '../../../constants/masterCertificates';
import {useAuth0} from "@auth0/auth0-react";

const AddMasterCertificate = (props) => {
  const userDispatch = useUserDispatch();

  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState();
  const [errorDetail, setErrorDetail] = useState();
  const [hasError, setHasError] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState(null);
  const [ledgerParties, setLedgerParties] = useState();
  const [, setCertifyingBodyParty] = useState(null);
  const [, setCertificateHolderParty] = useState(null);
  let currentOrg = JSON.parse(localStorage.getItem('current_org'));
  const { getAccessTokenSilently, logout } = useAuth0();

  useEffect(() => {
    if (window.location.pathname === `/master-certificates`) {
      tabActive(userDispatch, 0);
    }
    window.localStorage.setItem('main_tab', 'certificates');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {

    setLoading(true);

    listLedgerParties({ logout, getAccessTokenSilently })
      .then((res) => {
        buildLedgerPartiesList(res.data.content);
        const parties = res.data.content;

        if (props.masterCertificateId !== undefined) {

          listMasterCertificate({ logout, getAccessTokenSilently }, props.masterCertificateId)
            .then((res) => {
              setIsEdit(true);
                setCertifyingBodyParty(res.data.certifyingBodyPartyId);
                setCertificateHolderParty(res.data.certificateHolderPartyId);
                setFormData({
                  ...res.data,
                  certifyingBodyPartyID: res.data.certifyingBodyPartyId,
                  certifyingBodyPartyName: parties[0] !== undefined ? parties.find(el => el.party === currentOrg.party).partyName : '',
                  gisBoundary: res.data.gisBoundary ? JSON.stringify(res.data.gisBoundary) : null,
                  data: res.data.data ? JSON.stringify(res.data.data) : null,
                  activationDate: res.data.activationDate ? res.data.activationDate : '',
                  expirationDate: res.data.expirationDate ? res.data.expirationDate : ''
                })
          }).catch((err) => {
            setHasError(true);
            setErrorStatus(err.response.status);
            setErrorDetail(err.response.data.detail);
          })
        } else {
          setCertifyingBodyParty(currentOrg.party);
          setFormData({
            externalCertificateId: '',
            certifyingBodyPartyId: currentOrg.party,
            certifyingBodyPartyName: parties[0] !== undefined ? parties.find(el => el.party === currentOrg.party).partyName : '',
            certificateHolderPartyId: '',
            certificateName: '',
            certificateType: '',
            certificateVersion: '',
            certificateRating: '',
            assessorName: '',
            facilityId: '',
            status: '',
            activationDate: null,
            expirationDate: null,
            externalCertificateUrl: '',
            gisBoundary: null,
            data: null
          });
        }
      }).catch((err) => {
        setHasError(true);
        if (err.response?.data) {
          setErrorStatus(err.response?.data?.status);
          setErrorDetail(err.response?.data?.detail);
        }
        setLoading(false);
      })
    .finally(() => {
      setLoading(false);
    });   

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const buildLedgerPartiesList = (prods) => {
    const ledgerParties = prods.map((el, index) => {
      return { key: index, text: el.partyName, value: el.party};
    })
    setLedgerParties(ledgerParties);
  }

  const handleAddSubmit = (values, formikHelpers) => {

    values = {
      ...values,
      activationDate: values.activationDate ? values.activationDate : null,
      expirationDate: values.expirationDate ? values.expirationDate : null,
      data: JSON.parse(values.data),
      gisBoundary: JSON.parse(values.gisBoundary),
    }

    if(currentOrg && currentOrg.operator) {
      values = {
        ...values,
      }
    } else {
      delete values.certifyingBodyPartyName;
      delete values.certifyingBodyPartyId;
    }

    addMasterCertificate({ logout, getAccessTokenSilently }, values)
    .then((res) => {
      props.onCloseSuccessfully();
    })
    .catch((err) => {
      if (err.response.data['error.key'] !== null) {
        formikHelpers.setErrors({[err.response.data['error.key']]: err.response.data.detail});
        formikHelpers.setSubmitting(false);
      } else if(err.response.data.fieldErrors !== null) {
        let errors = {};
        err.response.data.fieldErrors.forEach(el => {
          errors[el.field] = el.message;
        })
        formikHelpers.setErrors(errors);
        formikHelpers.setSubmitting(false);
      } else {
        setHasError(true)
        setErrorStatus(err.response.status)
        setErrorDetail(err.response.data.detail);
      }
    })
    .finally(() => {
      setLoading(false);
    });
  }

  const handleEditSubmit = (values, formikHelpers) => {

    values = {
      externalCertificateId: values.externalCertificateId,
      certificateName: values.certificateName,
      certificateVersion: values.certificateVersion,
      certificateRating: values.certificateRating,
      assessorName: values.assessorName,
      facilityId: values.facilityId,
      status: values.status,
      activationDate: values.activationDate ? values.activationDate : null,
      expirationDate: values.expirationDate ? values.expirationDate : null,
      externalCertificateUrl: values.externalCertificateUrl,
      data: JSON.parse(values.data),
      gisBoundary: JSON.parse(values.gisBoundary),
    }

    editMasterCertificate({ logout, getAccessTokenSilently }, props.masterCertificateId, values)
    .then((res) => {
      props.onCloseSuccessfully();
    })
    .catch((err) => {
      if (err.response.data['error.key'] !== null) {
        formikHelpers.setErrors({[err.response.data['error.key']]: err.response.data.detail});
        formikHelpers.setSubmitting(false);
      } else if(err.response.data.fieldErrors !== null) {
        let errors = {};
        err.response.data.fieldErrors.forEach(el => {
          errors[el.field] = el.message;
        })
        formikHelpers.setErrors(errors);
        formikHelpers.setSubmitting(false);
      } else {
        setHasError(true)
        setErrorStatus(err.response.status)
        setErrorDetail(err.response.data.detail);
      }
    })
    .finally(() => {
      setLoading(false);
    });
  }

  return (
    <>
      {loading || !formData || !ledgerParties ? (
        <div className='empty-table'>
          <Loader active indeterminate size='small' />
        </div>
      ) : (
        <>
          { hasError ? (
              <Modal
                open={props.open}
                onClose={props.onClose}
                className='formModal'
              >
                <Modal.Header>{props.masterCertificateId ? 'Edit' : 'Add'} Master Certificate</Modal.Header>
                <Modal.Content className='formModal-content'>
                  <ErrorMessage statusCode={errorStatus} detail={errorDetail}></ErrorMessage>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={props.onClose}>
                      Close
                    </Button>
                  </Modal.Actions>
              </Modal>
            ) : (
              <Formik
                initialValues={formData}
                validationSchema={isEdit? editValidationSchema : addValidationSchema}
                onSubmit={(isEdit) ? handleEditSubmit : handleAddSubmit}
              >
                  { formProps => ( 
                    <form onSubmit={formProps.handleSubmit} noValidate >
                      <Modal
                        open={props.open}
                        onClose={props.onClose}
                        className='formModal'
                      >
                        <Modal.Header>{props.masterCertificateId ? 'Edit' : 'Add'} Master Certificate</Modal.Header>
                        <Modal.Content className='formModal-content'>
                          <Grid container spacing={2}>
                          { isEdit ?
                            <>
                              <Grid item md={12}>
                                <TextField 
                                  fullWidth={true}
                                  disabled
                                  label='Certifying Body Party'
                                  id='certifyingBodyPartyName'
                                  name='certifyingBodyPartyName'
                                  placeholder='Certifying Body Party'
                                  value={formProps.values.certifyingBodyPartyName ?? ''}
                                />
                              </Grid>
                              <Grid item md={12}>
                                <TextField 
                                  fullWidth={true}
                                  disabled
                                  label='Certificate Holder Party'
                                  id='certificateHolderPartyName'
                                  name='certificateHolderPartyName'
                                  placeholder='Certificate Holder Party'
                                  value={formProps.values.certificateHolderPartyName ?? ''}
                                />
                              </Grid>
                              <Grid item md={12}>
                                <TextField 
                                  fullWidth={true}
                                  disabled
                                  label='Certificate Type'
                                  id='certificateType'
                                  name='certificateType'
                                  placeholder='Certificate Type'
                                  value={formProps.values.certificateType ?? ''}
                                />
                              </Grid>
                            </>
                          :
                            <>
                              { currentOrg && currentOrg.operator &&
                                <Grid item md={12}>
                                  <Autocomplete
                                  id='certifyingBodyPartyId'
                                  fullWidth={true}
                                  disableClearable
                                  options={ledgerParties}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      required
                                      label='Certifying Body Party'
                                      error={formProps.touched.certifyingBodyPartyId && Boolean(formProps.errors.certifyingBodyPartyId)}
                                      helperText={formProps.touched.certifyingBodyPartyId && formProps.errors.certifyingBodycertifyingBodyPartyIdParty}
                                    />
                                  )}
                                  getOptionLabel={(option) => option.text}
                                  value={ledgerParties.find(el => el.value === formProps.values.certifyingBodyPartyId)}
                                  onChange={
                                    (_event, newLedgerParty) => {
                                      formProps.handleChange({target: {value: newLedgerParty.value, name: 'certifyingBodyPartyId'}});
                                      formProps.handleChange({target: {value: newLedgerParty.text, name: 'certifyingBodyPartyName'}});
                                      setCertifyingBodyParty(newLedgerParty.value);
                                    }
                                  }
                                />
                                </Grid>
                              }
                              <Grid item md={12}>
                                <Autocomplete
                                  id='certificateHolderPartyId'
                                  fullWidth={true}
                                  disableClearable
                                  options={ledgerParties}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      required
                                      label='Certificate Holder Party'
                                      error={formProps.touched.certificateHolderPartyId && Boolean(formProps.errors.certificateHolderPartyId)}
                                      helperText={formProps.touched.certificateHolderPartyId && formProps.errors.certificateHolderPartyId}
                                    />
                                  )}
                                  getOptionLabel={(option) => option.text}
                                  value={ledgerParties.find(el => el.value === formProps.values.certificateHolderPartyId)}
                                  onChange={
                                    (_event, newLedgerParty) => {
                                      formProps.handleChange({target: {value: newLedgerParty.value, name: 'certificateHolderPartyId'}});
                                      formProps.handleChange({target: {value: newLedgerParty.text, name: 'certificateHolderPartyName'}});
                                      setCertificateHolderParty(newLedgerParty.value);
                                    }
                                  }
                                />
                              </Grid>
                              <Grid item md={12}>
                                <TextField
                                  fullWidth={true}
                                  select
                                  required
                                  label='Certificate Type'
                                  name='certificateType'
                                  placeholder='Certificate Type'
                                  value={formProps.values.certificateType ?? ''}
                                  onChange={formProps.handleChange}
                                  error={formProps.touched.certificateType && Boolean(formProps.errors.certificateType)}
                                  helperText={formProps.touched.certificateType && formProps.errors.certificateType}
                                  >
                                    {masterCertificateTypes.map(el =>
                                      <MenuItem key={el.key} value={el.value}>{el.text}</MenuItem>
                                    )}
                                </TextField>
                              </Grid>
                            </>
                          }
                          <Grid item md={12}>
                            <TextField 
                              fullWidth={true}
                              label='Certificate Rating'
                              id='certificateRating'
                              name='certificateRating'
                              placeholder='Rating'
                              value={formProps.values.certificateRating ?? ''}
                              onChange={formProps.handleChange}
                            />
                          </Grid>
                          <Grid item md={12}>
                            <TextField 
                              fullWidth={true}
                              label='Certificate Version'
                              id='certificateVersion'
                              name='certificateVersion'
                              placeholder='Version'
                              value={formProps.values.certificateVersion ?? ''}
                              onChange={formProps.handleChange}
                            />
                          </Grid>
                          <Grid item md={12}>
                            <TextField
                              required
                              fullWidth={true}
                              label='Certificate Name'
                              id='certificateName'
                              name='certificateName'
                              placeholder='Name'
                              value={formProps.values.certificateName ?? ''}
                              onChange={formProps.handleChange}
                              error={formProps.touched.certificateName && Boolean(formProps.errors.certificateName)}
                              helperText={formProps.touched.certificateName && formProps.errors.certificateName}
                            />
                          </Grid>
                          <Grid item md={12}>
                            <TextField 
                              fullWidth={true}
                              label='External Certificate Id'
                              id='externalCertificateId'
                              name='externalCertificateId'
                              placeholder='External Certificate Id'
                              value={formProps.values.externalCertificateId ?? ''}
                              onChange={formProps.handleChange}
                            />
                          </Grid>
                          <Grid item md={12}>
                            <TextField 
                              fullWidth={true}
                              label='Assessor Name'
                              id='assessorName'
                              name='assessorName'
                              placeholder='Assessor Name'
                              value={formProps.values.assessorName ?? ''}
                              onChange={formProps.handleChange}
                            />
                          </Grid>
                          <Grid item md={12}>
                            <TextField 
                              fullWidth={true}
                              label='Facility Id'
                              id='facilityId'
                              name='facilityId'
                              placeholder='Facility Id'
                              value={formProps.values.facilityId ?? ''}
                              onChange={formProps.handleChange}
                            />
                          </Grid>
                          <Grid item md={12}>
                            <TextField
                              fullWidth={true}
                              select
                              required
                              label='Status'
                              name='status'
                              placeholder='Status'
                              value={formProps.values.status ?? ''}
                              onChange={formProps.handleChange}
                              error={formProps.touched.status && Boolean(formProps.errors.status)}
                              helperText={formProps.touched.status && formProps.errors.status}
                              >
                                {masterCertificateStatus.map(el =>
                                  <MenuItem key={el.key} value={el.value}>{el.text}</MenuItem>
                                )}
                            </TextField>
                          </Grid>
                          <Grid item md={12}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                format='MM-DD-YYYY'
                                id='activationDate'
                                name='activationDate'
                                label='Activation Date'
                                value={dayjs(dayjs(formProps.values.activationDate).format('MM-DD-YYYY'), 'MM-DD-YYYY', true).isValid() ? dayjs(formProps.values.activationDate) : null}
                                onChange={(e) => formProps.handleChange({target: {value: dayjs(e).format('YYYY-MM-DD')+'T00:00:00Z', name: 'activationDate'}})}
                                error={Boolean(formProps.errors.activationDate)}
                                helperText={formProps.errors.activationDate}
                              />
                            </LocalizationProvider>
                          </Grid>
                          <Grid item md={12}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                format='MM-DD-YYYY'
                                id='expirationDate'
                                name='expirationDate'
                                label='Expiration Date'
                                value={dayjs(dayjs(formProps.values.expirationDate).format('MM-DD-YYYY'), 'MM-DD-YYYY', true).isValid() ? dayjs(formProps.values.expirationDate) : null}
                                onChange={(e) => formProps.handleChange({target: {value: dayjs(e).format('YYYY-MM-DD')+'T00:00:00Z', name: 'expirationDate'}})}
                                error={Boolean(formProps.errors.expirationDate)}
                                helperText={formProps.errors.expirationDate}
                              />
                            </LocalizationProvider>
                          </Grid>
                          <Grid item md={12}>
                            <TextField 
                              fullWidth={true}
                              label='External Certificate URL'
                              id='facilityId'
                              name='externalCertificateUrl'
                              placeholder='External Certificate URL'
                              value={formProps.values.externalCertificateUrl ?? ''}
                              onChange={formProps.handleChange}
                              error={Boolean(formProps.errors.externalCertificateUrl)}
                              helperText={formProps.errors.externalCertificateUrl}
                            />
                          </Grid>
                          <Grid item md={12}>
                            <TextField
                                fullWidth={true}
                                label='Gis Boundary'
                                id='gisBoundary'
                                name='gisBoundary'
                                placeholder='Gis Boundary'
                                value={formProps.values.gisBoundary ?? ''}
                                onChange={
                                  (newGisBoundary) => {
                                    if(newGisBoundary.target.value === '')
                                      formProps.handleChange({target: {value: null, name: 'gisBoundary'}});
                                    else
                                      formProps.handleChange({target: {value: newGisBoundary.target.value, name: 'gisBoundary'}});
                                  }
                                }
                                error={Boolean(formProps.errors.gisBoundary)}
                                helperText={formProps.errors.gisBoundary}
                            />
                          </Grid>
                          <Grid item md={12}>
                            <TextField 
                              fullWidth={true}
                              label='Data'
                              id='data'
                              name='data'
                              placeholder='Data'
                              value={formProps.values.data ?? ''}
                              onChange={
                                (newData) => {
                                  if(newData.target.value === '')
                                    formProps.handleChange({target: {value: null, name: 'data'}});
                                  else
                                    formProps.handleChange({target: {value: newData.target.value, name: 'data'}});
                                }
                              }
                              error={Boolean(formProps.errors.data)}
                              helperText={formProps.errors.data}
                            />
                          </Grid>
                        </Grid>
                      </Modal.Content>
                      <Modal.Actions>
                        <Button disabled={formProps.isSubmitting} onClick={props.onClose}>Cancel</Button>
                        <Button disabled={formProps.isSubmitting} type='submit' className='actionButton' onClick={formProps.handleSubmit}>{ isEdit ? 'Save' : 'Add' }</Button>
                      </Modal.Actions>
                    </Modal>
                  </form>
                  )
                } 
              </Formik> 
            )
          }
        </>
      )}
    </>
  )
}

export default AddMasterCertificate

